<!--
  - SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->
<template>
	<tr v-if="row" :class="{ selected }">
		<td v-if="config.canSelectRows" :class="{sticky: config.canSelectRows}">
			<NcCheckboxRadioSwitch :model-value="selected" @update:model-value="v => $emit('update-row-selection', { rowId: row.id, value: v })" />
		</td>
		<td v-for="(col, index) in visibleColumns" :key="col.id"
			:style="{
				...getColumnWidthStyle(col),
				...getFrozenColumnStyle(col, index, pinnedColumnIndex, config.canSelectRows, visibleColumns, columnWidths),
			}"
			:class="{
				'search-result': getCell(col.id)?.searchStringFound,
				'filter-result': getCell(col.id)?.filterFound,
				'fixed-width': col.customSettings?.width > 0,
				'frozen-column': index <= pinnedColumnIndex,
				'frozen-column--last': index === pinnedColumnIndex,
			}"
			@click="handleCellClick(col)">
			<component :is="getTableCellComponent(col)"
				:column="col"
				:row-id="row.id"
				:value="getCellValue(col)"
				:element-id="elementId"
				:is-view="isView"
				:can-edit="config.canEditRows" />
		</td>
		<td v-if="config.showActions && (config.canEditRows || config.canDeleteRows || config.canCreateRows)"
			:class="{sticky: config.showActions}">
			<NcActions data-cy="rowActionMenu">
				<NcActionButton v-if="config.canEditRows"
					data-cy="editRowBtn"
					:close-after-click="true"
					@click="$emit('edit-row', row.id)">
					<template #icon>
						<Pencil :size="20" />
					</template>
					{{ t('tables', 'Edit row') }}
				</NcActionButton>
				<NcActionButton v-if="config.canCreateRows"
					data-cy="copyRowBtn"
					:close-after-click="true"
					@click="$emit('copy-row', row.id)">
					<template #icon>
						<ContentCopy :size="20" />
					</template>
					{{ t('tables', 'Copy row') }}
				</NcActionButton>
				<NcActionButton v-if="config.canDeleteRows"
					data-cy="deleteRowBtn"
					:close-after-click="true"
					@click="$emit('delete-row', row.id)">
					<template #icon>
						<TrashCanOutline :size="20" />
					</template>
					{{ t('tables', 'Delete row') }}
				</NcActionButton>
			</NcActions>
		</td>
	</tr>
</template>

<script>
import { NcCheckboxRadioSwitch, NcActions, NcActionButton } from '@nextcloud/vue'
import ContentCopy from 'vue-material-design-icons/ContentCopy.vue'
import Pencil from 'vue-material-design-icons/PencilOutline.vue'
import TrashCanOutline from 'vue-material-design-icons/TrashCanOutline.vue'
import { getTableCellComponent } from './TableCell.js'
import { getColumnWidthStyle, getFrozenColumnStyle } from './../mixins/columnHandler.js'
import { translate as t } from '@nextcloud/l10n'
import {
	TYPE_META_ID, TYPE_META_CREATED_BY, TYPE_META_CREATED_AT, TYPE_META_UPDATED_BY, TYPE_META_UPDATED_AT,
} from '../../../constants.ts'
import activityMixin from '../../../mixins/activityMixin.js'

export default {
	name: 'TableRow',
	components: {
		NcActions,
		NcActionButton,
		ContentCopy,
		Pencil,
		TrashCanOutline,
		NcCheckboxRadioSwitch,
	},

	mixins: [activityMixin],
	props: {
		row: {
			type: Object,
			default: () => {},
		},
		columns: {
			type: Array,
			default: () => [],
		},
		selected: {
			type: Boolean,
			default: false,
		},
		viewSetting: {
			type: Object,
			default: null,
		},
		config: {
			type: Object,
			default: null,
		},
		elementId: {
			type: Number,
			default: null,
		},
		isView: {
			type: Boolean,
			default: true,
		},
		pinnedColumnId: {
			type: Number,
			default: null,
		},
		columnWidths: {
			type: Object,
			default: null,
		},
	},
	emits: [
		'copy-row',
		'delete-row',
		'edit-row',
		'update-row-selection',
	],
	computed: {
		getSelection: {
			get: () => { return this.selected },
			set: () => { alert('updating selection') },
		},
		visibleColumns() {
			return this.columns.filter(col => !this.viewSetting?.hiddenColumns?.includes(col.id))
		},
		pinnedColumnIndex() {
			if (this.pinnedColumnId === null) return -1
			return this.visibleColumns.findIndex(col => col.id === this.pinnedColumnId)
		},
		// column types that don't support inline editing yet
		// to be used to trigger the edit modal instead of inline editing
		nonInlineEditableColumnTypes() {
			return [
			]
		},
	},
	methods: {
		t,
		getColumnWidthStyle,
		getFrozenColumnStyle,
		getTableCellComponent,
		handleCellClick(column) {
			// If the column type doesn't support inline editing, trigger the edit modal
			if (this.nonInlineEditableColumnTypes.includes(column.type) && this.config.canEditRows) {
				this.$emit('edit-row', this.row.id)
			}
		},
		getCell(columnId) {
			if (columnId < 0) {
				// See metaColumns.js for mapping
				let value
				switch (columnId) {
				case TYPE_META_ID:
					value = this.row.id
					break
				case TYPE_META_CREATED_BY:
					value = this.row.createdBy
					break
				case TYPE_META_UPDATED_BY:
					value = this.row.lastEditBy
					break
				case TYPE_META_CREATED_AT:
					value = this.row.createdAt
					break
				case TYPE_META_UPDATED_AT:
					value = this.row.lastEditAt
					break
				}
				return { columnId, value }
			}
			return this.row.data.find(item => item.columnId === columnId) || null
		},
		getCellValue(column) {
			if (!this.row) {
				return null
			}

			// lets see if we have a value
			const cell = this.getCell(column.id)
			let value

			if (cell) {
				value = cell.value
			} else {
				// if no value is given, try to get the default value from the column definition
				value = column.default()
			}

			return column.parseValue(value)
		},
		truncate(text) {
			if (text.length >= 400) {
				return text.substring(0, 400) + '...'
			} else {
				return text
			}
		},
	},
}
</script>

<style scoped lang="scss">

tr.selected {
	background-color: var(--color-primary-light) !important;
}

:deep(.search-result > div) {
	background-color: var(--color-primary-element-light);
	border-radius: var(--border-radius-large);
	padding: calc(var(--default-grid-baseline) * 3);
}

:deep(.checkbox-radio-switch__icon) {
	margin: 0;
}

td.fixed-width {
	overflow: hidden;
	white-space: normal;
}

</style>
